import { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Eye } from 'lucide-react';
import { Button, Field, Input, Select, Badge } from './UI';

/** The input kinds the server accepts — mirrors FIELD_TYPES in constants.js. */
export const FIELD_TYPES = [
  ['text', 'Short text'],
  ['textarea', 'Long text'],
  ['email', 'Email'],
  ['tel', 'Phone'],
  ['number', 'Number'],
  ['date', 'Date'],
  ['time', 'Time'],
  ['select', 'Dropdown'],
  ['radio', 'Single choice'],
  ['checkbox', 'Tick box (multi-select with choices)'],
  ['file', 'Attachment'],
];

const NEEDS_OPTIONS = ['select', 'radio', 'checkbox'];

/** What a field's answer can be matched onto, so it lines up with the roster. */
const MAPS_TO = [
  ['none', 'Nothing'],
  ['name', 'Name'],
  ['email', 'Email'],
  ['mobile', 'Mobile'],
  ['employeeCode', 'Employee code'],
  ['officeLocation', 'Office location'],
  ['gender', 'Gender'],
  ['idProof', 'ID proof'],
  ['companions', 'People along with them'],
];

const BLANK_FIELD = {
  label: '', type: 'text', required: false, options: [], visibleToHotel: false, mapsTo: 'none',
};

/**
 * Edits a form's field list. Controlled — the parent owns `fields` and decides
 * when to save, so the same builder serves a new form and an existing one.
 */
export default function FormBuilder({ fields, onChange }) {
  const [open, setOpen] = useState(null);

  const patch = (i, changes) => onChange(fields.map((f, n) => (n === i ? { ...f, ...changes } : f)));
  const add = () => { onChange([...fields, { ...BLANK_FIELD }]); setOpen(fields.length); };
  const remove = (i) => { onChange(fields.filter((_, n) => n !== i)); setOpen(null); };

  const move = (i, dir) => {
    const to = i + dir;
    if (to < 0 || to >= fields.length) return;
    const next = [...fields];
    [next[i], next[to]] = [next[to], next[i]];
    onChange(next);
    setOpen(to);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {!fields.length && (
        <p style={{ fontSize: 12.5, color: 'var(--text-lo)', padding: '10px 2px' }}>
          No questions yet. Add the first one below.
        </p>
      )}

      {fields.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={i} style={{
            border: '1px solid var(--border)', borderRadius: 'var(--r)',
            background: 'var(--ink-800)', overflow: 'hidden',
          }}>
            {/* header row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 11px' }}>
              <button type="button" onClick={() => setOpen(isOpen ? null : i)}
                style={{ flex: 1, minWidth: 0, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 13, color: f.label ? 'var(--text-hi)' : 'var(--text-lo)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {f.label || 'Untitled question'}
                </span>
                <Badge tone="neutral">{(FIELD_TYPES.find(([v]) => v === f.type) || [])[1] || f.type}</Badge>
                {f.required && <Badge tone="gold">Required</Badge>}
                {f.visibleToHotel && <Badge tone="info"><Eye size={10} /> Hotel</Badge>}
              </button>

              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                style={{ color: 'var(--text-lo)', padding: 5, opacity: i === 0 ? 0.3 : 1 }}>
                <ChevronUp size={14} />
              </button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === fields.length - 1} title="Move down"
                style={{ color: 'var(--text-lo)', padding: 5, opacity: i === fields.length - 1 ? 0.3 : 1 }}>
                <ChevronDown size={14} />
              </button>
              <button type="button" onClick={() => remove(i)} title="Remove question"
                style={{ color: 'var(--danger)', padding: 5 }}>
                <Trash2 size={14} />
              </button>
            </div>

            {/* editor */}
            {isOpen && (
              <div style={{
                padding: '4px 11px 13px', display: 'flex', flexDirection: 'column', gap: 12,
                borderTop: '1px solid var(--border)',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.6fr) minmax(0,1fr)', gap: 11 }}>
                  <Field label="Question">
                    <Input value={f.label} placeholder="e.g. Aadhaar number"
                      onChange={(e) => patch(i, { label: e.target.value })} />
                  </Field>
                  <Field label="Answer type">
                    <Select value={f.type} onChange={(e) => patch(i, { type: e.target.value, options: [] })}>
                      {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </Select>
                  </Field>
                </div>

                {NEEDS_OPTIONS.includes(f.type) && (
                  <Field
                    label="Choices"
                    hint={f.type === 'checkbox'
                      ? 'One per line or comma separated — people can pick several'
                      : 'One per line, or comma separated'}
                  >
                    <textarea
                      value={(f.options || []).join('\n')}
                      onChange={(e) => patch(i, {
                        options: e.target.value.split(/[\n,]/).map((o) => o.trimStart()),
                      })}
                      rows={3}
                      style={{
                        width: '100%', padding: '11px 13px', background: 'var(--ink-900)',
                        border: '1px solid var(--border)', borderRadius: 'var(--r)',
                        fontSize: 13.5, outline: 'none', resize: 'vertical', colorScheme: 'dark',
                      }}
                    />
                  </Field>
                )}

                <Field label="Match this answer to" hint="Lets the answer line up with the uploaded roster">
                  <Select value={f.mapsTo} onChange={(e) => patch(i, { mapsTo: e.target.value })}>
                    {MAPS_TO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </Select>
                </Field>

                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={f.required}
                      onChange={(e) => patch(i, { required: e.target.checked })}
                      style={{ accentColor: 'var(--gold-400)' }} />
                    Required
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={f.visibleToHotel}
                      onChange={(e) => patch(i, { visibleToHotel: e.target.checked })}
                      style={{ accentColor: 'var(--gold-400)' }} />
                    Hotel can see this answer
                  </label>
                </div>
                {f.visibleToHotel && (
                  <p style={{ fontSize: 11.5, color: 'var(--warn)', lineHeight: 1.6 }}>
                    This answer is shared with the hotel's rooming desk. Only tick it for
                    what they genuinely need to allocate a room.
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}

      <Button variant="outline" size="sm" icon={Plus} onClick={add} style={{ alignSelf: 'flex-start' }}>
        Add question
      </Button>
    </div>
  );
}
